<template>
  <HeadTitle
    title="Reporte de cartas y certificaciones generadas"
    description="Lista de todas las cartas generadas en el sistema"
  />
  <DataTable
    :value="letterRequests"
    dataKey="id"
    v-model:filters="filters"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    filter-display="row"
    tableStyle="min-width: 60rem"
    currentPageReportTemplate="{first} a {last} de {totalRecords}"
    class="tabla mb-3"
    paginator
    :rows="10"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    removable-sort
    sort-field="updatedAt"
    :sort-order="-1"
    :globalFilterFields="[
      'employee.name',
      'employee.code',
      'employee.payroll.code',
      'employee.payroll.name',
      'letterCertifConfig.name',
      'createdAt',
    ]"
  >
    <template #header>
      <div class="flex justify-content-end">
        <Button
          icon="pi pi-file-excel"
          label="Exportar"
          @click="exportCSV()"
          class="text-sm font-light bg-green-100 border-green-100 text-green-700 mr-3"
        />

        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="filters['global'].value" placeholder="Buscar" />
        </span>
      </div>
    </template>
    <Column
      field="employee.payroll"
      header="Código de Nómina"
      sortable
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
      filter
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{
          data.employee.payroll.code
        }}</span>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="payrollsCode"
          optionLabel="code"
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
          filter
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option.code }}</span>
            </div>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column
      field="employee.payroll"
      header="Nómina"
      sortable
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
      filter
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{
          data.employee.payroll.name
        }}</span>
      </template>
    </Column>
    <Column
      field="employee"
      header="Código del Empleado"
      sortable
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{ data.employee.code }}</span>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <MultiSelect
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="employeesCode"
          option-label="code"
          placeholder=""
          class="p-column-filter"
          style="min-width: 14rem"
          :maxSelectedLabels="1"
          filter
        >
          <template #option="slotProps">
            <div class="flex align-items-center gap-2">
              <span>{{ slotProps.option.code }}</span>
            </div>
          </template>
        </MultiSelect>
      </template>
    </Column>
    <Column
      field="employee.name"
      header="Nombre del Empleado"
      sortable
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '14rem' }"
      style="min-width: 14rem"
      filter
    >
      <template #body="{ data, field }">
        <span class="font-medium text-800">{{ data.employee.name }}</span>
      </template>
    </Column>
    <Column
      field="letterCertifConfig.name"
      header="Nombre de la carta"
      style="width: 20%"
      sortable
    ></Column>
    <Column field="createdAt" header="Fecha de creación" sortable>
      <template #body="{ data, field }">
        {{ formatDateWithHour(data[field].toString()) }}
      </template>
    </Column>
    <Column field="generationDate" header="Fecha de generación">
      <template #body="{ data, field }">
        <span v-if="data[field]">
          {{ formatDateWithHour(data.resolutionDate) }}</span
        >
        <span v-else>{{ 'No definida' }}</span>
      </template>
    </Column>
    <Column field="status" header="Estado" style="width: 20%" sortable>
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
          class=""
        ></Tag>
      </template>
    </Column>
    <Column header="Nombre de Aprobador">
      <template #body="{ data }">
        <span>{{ data.letterCertifConfig?.approver?.name }}</span>
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización" sortable>
      <template #body="{ data, field }">
        {{ formatDateWithHour(data[field].toString()) }}
      </template>
    </Column>
    <!-- <Column header="Acciones" field="steps">
            <template #body="{ data }">
                <div class="flex flex-justify-end">
                    <Button v-tooltip.bottom="'Ver detalles'" icon="pi pi-eye" class="h-2rem hover:text-blue-800"
                        severity="primary" @click="goToDetail(data['id'])" />
                </div>
            </template>
        </Column> -->
  </DataTable>
  <Footer>
    <Button
      class="mt-2 font-light p-button-text"
      icon="pi pi-angle-left"
      label="Volver"
      @click="router.push('/letterConfigs')"
    ></Button>
  </Footer>
</template>
<script lang="ts" setup>
  import { onMounted, ref } from 'vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import useCompany from '@/store/company'
  import useLoading from '@/store/load'
  import Tag from 'primevue/tag'
  import { storeToRefs } from 'pinia'
  import { getLettersGeneratedReports } from '../services/ReportServices'
  import Footer from '@/shared/components/Footer.vue'
  import { FilterMatchMode } from 'primevue/api'
  import LetterRequest from '@/modules/letters/models/LetterRequest'
  import { formatDateWithHour } from '@/shared/utils/fomartDateWithHours'
  import formatRequestState from '@/shared/utils/formatRequestState'
  import router from '@/router'
  import LetterRequestReportXlsx from '@/shared/utils/reports/letterRequestReportXlsx'

  const companyStore = useCompany()
  const loadingStore = useLoading()
  const { company } = storeToRefs(companyStore)

  const letterRequests = ref<LetterRequest[]>()
  const filteredRequests = ref<LetterRequest[]>()
  const letterRequestPivot = ref<LetterRequest[]>()

  //filters
  const employeesCode = ref()
  const letterStatus = ref()
  const payrollsCode = ref()
  /////

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    await refreshData()
  })

  onMounted(async () => {
    await refreshData()
  })

  const refreshData = async () => {
    loadingStore.setLoadingState()
    const data = await getLettersGeneratedReports(company.value?.id)
    letterRequests.value = data
    filteredRequests.value = data
    letterRequestPivot.value = data
    setFilters(letterRequests.value)
    loadingStore.dismissLoadingState()
  }

  const setFilters = (requests: any) => {
    employeesCode.value = requests.reduce((acc: any, curr: any) => {
      if (!acc.find((e: any) => e.id === curr.employee.id)) {
        acc.push(curr.employee)
      }
      return acc
    }, [])

    payrollsCode.value = requests.reduce((acc: any, curr: any) => {
      if (!acc.find((e: any) => e.id === curr.employee.payroll.id)) {
        acc.push(curr.employee.payroll)
      }
      return acc
    }, [])
  }

  const goToDetail = (id: string) => {
    router.push('/letterRequests/' + id)
  }

  const filters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: [], matchMode: FilterMatchMode.IN },
    employee: { value: null, matchMode: FilterMatchMode.IN },
    'employee.code': { value: null, matchMode: FilterMatchMode.IN },
    'employee.payroll': { value: null, matchMode: FilterMatchMode.IN },
  })

  const exportCSV = () => {
    if (!filteredRequests.value) return
    LetterRequestReportXlsx(filteredRequests.value)
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'REJECTED':
        return 'danger'
      case 'GENERATED':
        return 'info'
      default:
        return 'warning'
    }
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }
</style>
